// Thumbnail-forward library card. A 16:9 poster (thumbnail_url -> YouTube frame -> branded
// gradient) leads, with a completion check and format badge overlaid; feature/New chips, title,
// description and accurate format indicators sit below. Navigation is a Link (brand rule), so the
// whole card is a real anchor to the reader.

import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Card, CardActionArea, CardContent, Chip, Skeleton, Stack, Typography } from '@mui/material';
import { BiPlayCircle, BiFile, BiCheck } from 'react-icons/bi';
import { extractYouTubeId } from '../utils/youtube';
import { useModuleThumbnail } from '../hooks/useModuleThumbnail';
import { isRecentlyReleased } from '../utils/recency';
import { featureLabel, type ModuleSummary } from '../types';

// Fallback poster when a module has neither a thumbnail nor a video frame — brand secondaryAccent
// into grounding, so an image-less card still looks deliberate rather than blank.
const GRADIENT = 'linear-gradient(150deg, #4068a0 0%, #202430 100%)';

export default function ModuleCard({ module, completed = false }: { module: ModuleSummary; completed?: boolean }) {
  const hasVideo = Boolean(extractYouTubeId(module.hero_video_url));
  const hasDoc = Boolean(module.attachment_url);
  const isNew = isRecentlyReleased(module, new Date());
  const { src, loading } = useModuleThumbnail(module);
  const [imgError, setImgError] = useState(false);
  const showImage = Boolean(src) && !imgError;

  return (
    <Card sx={{ height: '100%', transition: 'all 150ms ease', '&:hover': { transform: 'translateY(-3px)', boxShadow: 4 } }}>
      <CardActionArea
        component={RouterLink}
        to={`/learning-center/${encodeURIComponent(module.key)}`}
        sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
      >
        {/* Poster */}
        <Box sx={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', overflow: 'hidden', background: GRADIENT }}>
          {loading ? (
            <Skeleton variant="rectangular" animation="wave" sx={{ width: '100%', height: '100%' }} />
          ) : showImage ? (
            <Box
              component="img"
              src={src as string}
              alt=""
              loading="lazy"
              onError={() => setImgError(true)}
              sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : null}

          {/* Center glyph: play for video, document otherwise */}
          {!loading && (
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.92)' }}>
              {hasVideo ? <BiPlayCircle size={44} /> : !showImage && hasDoc ? <BiFile size={34} /> : null}
            </Box>
          )}

          {/* Format badge */}
          {(hasVideo || hasDoc) && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{
                position: 'absolute', bottom: 8, left: 8,
                px: 1, py: '3px', borderRadius: 1,
                bgcolor: 'rgba(0,0,0,0.55)', color: 'common.white',
              }}
            >
              {hasVideo ? <BiPlayCircle size={13} /> : <BiFile size={13} />}
              <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.03em', color: 'inherit' }}>
                {hasVideo ? 'Video' : 'Document'}
              </Typography>
            </Stack>
          )}

          {/* Completion check */}
          {completed && (
            <Box
              aria-label="Completed"
              sx={{
                position: 'absolute', top: 8, right: 8,
                width: 26, height: 26, borderRadius: '50%',
                bgcolor: 'success.main', color: 'common.white',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: 2,
              }}
            >
              <BiCheck size={18} />
            </Box>
          )}
        </Box>

        {/* Body */}
        <CardContent sx={{ display: 'flex', flexDirection: 'column', width: '100%', flexGrow: 1, gap: 0.75 }}>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
            <Chip size="small" label={featureLabel(module.feature)} sx={{ fontWeight: 600 }} />
            {isNew && (
              <Chip size="small" variant="outlined" color="primary" label="New" sx={{ fontWeight: 700 }} />
            )}
          </Stack>

          <Typography sx={{ fontWeight: 700, fontSize: 16, lineHeight: 1.3 }}>{module.name}</Typography>
          {module.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
            >
              {module.description}
            </Typography>
          )}

          <Box sx={{ flexGrow: 1 }} />
          <Stack direction="row" spacing={1.5} sx={{ mt: 1, color: 'text.disabled' }}>
            {hasVideo && (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <BiPlayCircle size={14} />
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>Video</Typography>
              </Stack>
            )}
            {hasDoc && (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <BiFile size={14} />
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>Document</Typography>
              </Stack>
            )}
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
